// src/pages/professional/Dashboard.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { StatusBadge, Button, EmptyState } from 'shared/components/ui';
import { formatCurrency, isToday } from 'shared/utils/helpers';
import { useMobile } from 'shared/hooks/useMobile';
import { appointmentsService } from 'infrastructure/firebase';

export default function ProfessionalDashboard() {
  const isMobile = useMobile();
  const { userProfile } = useAuth();
  const { appointments, showToast } = useApp();
  const navigate = useNavigate();

  const today = new Date().toISOString().split('T')[0];
  const myAppts = appointments.filter(a => a.professionalId === userProfile?.id);
  // Normaliza data para suportar tanto campo 'date' (string) quanto 'dateTime' (Timestamp)
  const todayAppts = myAppts.filter(a => {
    const d = a.dateTime?.toDate?.()?.toISOString().split('T')[0] || a.date;
    return d === today;
  });
  const upcoming   = myAppts.filter(a => a.date >= today && !isToday(a.date)).slice(0, 5);

  const handleMarkDone = async (appt) => {
    try {
      await appointmentsService.markDone(appt.id);
      showToast(`Atendimento de ${appt.clientName} marcado como realizado!`);
    } catch { showToast('Erro ao marcar atendimento.', 'danger'); }
  };

  const monthRevenue = myAppts
    .filter(a => a.done && a.date?.startsWith(new Date().toISOString().slice(0, 7)))
    .reduce((s, a) => s + Number(a.price || 0), 0);

  const statCard = (bg, fg) => ({
    background: '#fff', border: '1px solid #E8E5EC', borderRadius: 16,
    padding: '20px 24px', position: 'relative', overflow: 'hidden',
  });
  const weekCount = myAppts.filter(a => {
    const d = new Date(a.date + 'T12:00:00'); const now = new Date();
    const start = new Date(now); start.setDate(now.getDate() - now.getDay());
    return d >= start;
  }).length;

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      {/* Welcome */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 32, color: '#3D3447', marginBottom: 4 }}>
          Olá, {userProfile?.name?.split(' ')[0]}! 👋
        </div>
        <div style={{ color: '#A8A1B0', fontSize: 14 }}>
          {new Date().toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 28 }}>
        {[
          { label:'Hoje', value: todayAppts.length, sub:'atendimentos', icon:'📅', accent:'#6B5876' },
          { label:'Receita do Mês', value: formatCurrency(monthRevenue), sub:'✓ realizados', icon:'💰', accent:'#2F5C42' },
          { label:'Esta Semana', value: weekCount, sub:'agendamentos', icon:'📊', accent:'#1e40af' },
          { label:'Aguardando confirmação', value: myAppts.filter(a=>a.status==='waitingConfirm').length, sub:'pendentes', icon:'⏳', accent:'#854D0E' },
        ].map((s, i) => (
          <div key={i} style={{ ...statCard(), display:'flex', flexDirection:'column', gap:4 }}>
            <div style={{ position:'absolute', right:16, top:16, fontSize:24, opacity:0.15 }}>{s.icon}</div>
            <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#A8A1B0' }}>{s.label}</div>
            <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32, color: s.accent, lineHeight:1.1 }}>{s.value}</div>
            <div style={{ fontSize:12, color:'#A8A1B0' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24 }}>
        {/* Today */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Agenda de Hoje</div>
              <div className="card-subtitle">{todayAppts.length} atendimentos</div>
            </div>
            <Button variant="primary" size="sm" onClick={() => navigate('/pro/new-appointment')}>+ Novo</Button>
          </div>
          <div>
            {todayAppts.length === 0 ? (
              <EmptyState icon="🌟" title="Dia livre!" text="Nenhum atendimento hoje." />
            ) : (
              todayAppts.map(a => (
                <div key={a.id} style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, flexShrink: 0, textAlign: 'center', background: 'var(--surface-2)', borderRadius: 10, padding: '8px 4px' }}>
                    <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 16 }}>{a.time}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.clientName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>💆 {a.procedureName} • {a.duration}min</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <StatusBadge status={a.status} />
                    {!a.done && a.status === 'confirmed' && (
                      <Button variant="success" size="sm" onClick={() => handleMarkDone(a)}>✓ Realizado</Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Próximos Agendamentos</div>
            <Button variant="secondary" size="sm" onClick={() => navigate('/pro/schedule')}>Ver agenda →</Button>
          </div>
          <div>
            {upcoming.length === 0 ? (
              <EmptyState icon="📅" title="Sem agendamentos futuros" />
            ) : (
              upcoming.map(a => (
                <div key={a.id} style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 44, flexShrink: 0, textAlign: 'center', background: 'var(--surface-2)', borderRadius: 10, padding: '6px 4px' }}>
                    <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 18 }}>{a.date?.split('-')[2]}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                      {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][Number(a.date?.split('-')[1]) - 1]}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.clientName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.procedureName} • {a.time}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
